import { request } from 'api/requests';
import {
  AuthMutationResponse,
  ExportCSVQueryParams,
  IDValueDtoLongString,
  ItineraryRequest,
  ItineraryResponse,
  OfferPriceRequest,
  OperatorsDriversPaginationQueryParams,
  OperatorsInvoicesPaginationQueryParams,
  OperatorsUserMessagingMessagesQueryParams,
  OperatorsUserMessagingUsersQueryParams,
  OperatorsVehiclesLocationsQueryParams,
  OperatorsVehiclesPaginationQueryParams,
  PageDto,
  PaginationQueryParams,
  RideMoveRequest,
  ScheduleQueryParams,
  UserAccessDto,
  VehicleActivationDto,
} from 'api/types';
import { AxiosResponse } from 'axios';
import {
  AccountInvitationRegistrationSchemaType,
  AccountUserSchemaType,
  FeedbackRequestSchemaType,
  OperatorBankAccountDtoSchemaType,
  PasswordSchemaType,
} from 'components/forms/AccountFactory/types';
import { LoginSchemaType } from 'components/forms/LoginForm/types';
import {
  DriverDtoSchema,
  DriverWithStatusDtoSchemaType,
  OperatorRideDtoSchemaType,
  OrderDetailsSchemaPayloadType,
  RideDtoSchemaType,
  VehicleDtoSchema,
  VehicleWithStatusDtoSchema,
} from 'components/forms/BookingFormFactory/types';
import {
  BusStatus,
  ContactInfoSchemaType,
  Driver,
  FileType,
  Invoice,
  LegSchemaType,
  MessageGroup,
  MessageGroupMember,
  OfferSchemaType,
  SeenStatus,
  UserMessage,
  UserRole,
  Vehicle,
  VehicleClass,
  OrderDtoSchemaType,
  ClientSchemaType,
} from 'remote/global';
import Cookies from 'js-cookie';
import {
  Alert,
  CorporateCharter,
  CorporateCharterRequest,
  DriverCandidate,
  FileResponse,
  GoogleConfig,
  OffersSettings,
  OperatorRegistrationPayload,
  OrderRequestPayload,
  PricingDetails,
  RideWithScheduleItem,
  ScheduleFilterStreamingResponse,
  ScheduleItem,
  StopBasedItinerary,
  StringOrNumber,
  VehicleCandidate,
  VehicleLocation,
  VehicleSize,
  VehicleTemporaryBase,
  OperatorOfferWithRank,
  OperatorOfferByRefNumber,
  PlanDto,
  OperatorPlanDto,
  MileageRevenueSummary,
} from 'utils/data/data-types';
import { CookieKeyEnum } from 'utils/data/enums';
import {
  ChangeRideEndSchemaType,
  RideOffersSchemaType,
} from 'components/forms/RideFormFactory/types';
import {
  CalculatorResponseDtoSchemaType,
  PricingCalculatorSchemaType,
  PricingPeriodSchemaType,
  PricingRulesDtoSchemaType,
} from 'components/forms/CarrierFormFactory/types';
import {
  OperatorDtoSchemaType,
  RegistrationResponseSchemaType,
  ScheduleItemSchemaType,
  SendOfferToClientRequestSchemaType,
  TokenStatusResponseSchemaType,
  VehicleTemporaryBaseDtoSchemaType,
} from 'components/forms/types';
import { CorporateCharterItinerarySchemaType } from 'components/forms/CorporateCharter/types';
import {
  MessageGroupSchemaType,
  NewMessageGroupSchemaType,
  UserMessageDtoSchemaType,
} from 'components/forms/MessagingFactory/types';

export const postAuthLogin = (
  data: LoginSchemaType
): Promise<AxiosResponse<AuthMutationResponse>> =>
  request({
    method: 'POST',
    url: `/auth/login`,
    data,
  });

export const postAuthRefresh = (
  potentialToken?: string | null | void
): Promise<AxiosResponse<AuthMutationResponse>> => {
  const refreshToken = potentialToken ?? Cookies.get(CookieKeyEnum.REFRESH_TOKEN);
  return request({
    method: 'POST',
    url: '/auth/refresh',
    data: { refreshToken },
  });
};

export const fetchAuth = (): Promise<AxiosResponse<UserAccessDto>> =>
  request({
    url: `/users/current/accesses`,
  });

export const putUsersPassword = (
  userId: string,
  data: PasswordSchemaType
): Promise<AxiosResponse<ContactInfoSchemaType>> => {
  return request({
    method: 'PUT',
    url: `/users/${userId}/password`,
    data,
  });
};

export type ResetTokenMutationProps = {
  email: string;
};

export const postAuthPasswordResetToken = (
  data: ResetTokenMutationProps
): Promise<AxiosResponse<null>> =>
  request({
    method: 'POST',
    url: `/auth/password-reset-token`,
    data,
  });

export const fetchMapsGoogleConfiguration = (): Promise<AxiosResponse<GoogleConfig>> =>
  request({
    url: `/maps/google/configuration`,
  });

export const fetchOffersSettings = (): Promise<AxiosResponse<OffersSettings>> =>
  request({
    url: `/offers/settings`,
  });

export const fetchUsersContactInfo = (
  userId: string
): Promise<AxiosResponse<ContactInfoSchemaType>> =>
  request({
    url: `/users/${userId}/contactInfo`,
  });

export const putUsersContactInfo = (
  userId: string,
  data: ContactInfoSchemaType
): Promise<AxiosResponse<ContactInfoSchemaType>> => {
  return request({
    method: 'PUT',
    url: `/users/${userId}/contactInfo`,
    data,
  });
};

export const fetchOperatorsById = (
  operatorId: string
): Promise<AxiosResponse<OperatorDtoSchemaType>> =>
  request({
    url: `/operators/${operatorId}`,
  });

export const putOperatorsById = (
  operatorId: string,
  data: OperatorDtoSchemaType
): Promise<AxiosResponse<OperatorDtoSchemaType>> => {
  return request({
    method: 'PUT',
    url: `/operators/${operatorId}`,
    data,
  });
};

export const fetchOperatorsUsers = (
  operatorId: string,
  params: PaginationQueryParams
): Promise<AxiosResponse<PageDto<AccountUserSchemaType[]>>> =>
  request({
    url: `/operators/${operatorId}/users`,
    params: new URLSearchParams(params),
  });

export const fetchOperatorsScheduleVehicles = (
  operatorId: string,
  params: ScheduleQueryParams
): Promise<AxiosResponse<ScheduleFilterStreamingResponse>> =>
  request({
    url: `/operators/${operatorId}/schedule/vehicles/filter`,
    params: new URLSearchParams(params),
  });

export const fetchOperatorsScheduleVehiclesRepairLog = (
  operatorId: string,
  vehicleId: string,
  params: PaginationQueryParams
): Promise<AxiosResponse<PageDto<ScheduleItem>>> =>
  request({
    url: `/operators/${operatorId}/schedule/vehicles/${vehicleId}/repair-log`,
    params: new URLSearchParams(params),
  });

export const fetchOperatorsScheduleDrivers = (
  operatorId: string,
  params: ScheduleQueryParams
): Promise<AxiosResponse<ScheduleFilterStreamingResponse>> =>
  request({
    url: `/operators/${operatorId}/schedule/drivers/filter`,
    params: new URLSearchParams(params),
  });

export const fetchOperatorsScheduleDriversWork = (
  operatorId: string,
  params: ScheduleQueryParams
): Promise<AxiosResponse<ScheduleFilterStreamingResponse>> =>
  request({
    url: `/operators/${operatorId}/schedule/drivers/work`,
    params: new URLSearchParams(params),
  });

export const postOperatorOrders = (
  operatorId: string,
  data: OrderRequestPayload
): Promise<AxiosResponse<OrderDtoSchemaType>> =>
  request({
    method: 'POST',
    url: `/operators/${operatorId}/orders`,
    data,
  });

export const fetchOperatorsOrdersById = (
  operatorId: string,
  identifier: StringOrNumber
): Promise<AxiosResponse<OrderDtoSchemaType>> =>
  request({
    url: `/operators/${operatorId}/orders/${identifier}`,
  });

export const patchOperatorsOrdersDetails = (
  operatorId: string,
  identifier: StringOrNumber,
  data: OrderDetailsSchemaPayloadType
): Promise<AxiosResponse<OrderDtoSchemaType>> =>
  request({
    method: 'PATCH',
    url: `/operators/${operatorId}/orders/${identifier}`,
    data,
  });

export const deleteOperatorsOrdersByIdentifier = (
  operatorId: string,
  identifier: StringOrNumber
): Promise<AxiosResponse<number>> =>
  request({
    method: 'DELETE',
    url: `/operators/${operatorId}/orders/${identifier}`,
  });

export const postOperatorsRidesLegsByIndex = (
  operatorId: string,
  rideId: string,
  data: LegSchemaType & { legIndex: number }
): Promise<AxiosResponse<RideWithScheduleItem>> =>
  request({
    method: 'POST',
    url: `/operators/${operatorId}/rides/${rideId}/legs/${data.legIndex}`,
    data,
  });

export const deleteOperatorsRidesLegsByIndex = (
  operatorId: string,
  rideId: string,
  legIndex: number
): Promise<AxiosResponse<RideWithScheduleItem>> =>
  request({
    method: 'DELETE',
    url: `/operators/${operatorId}/rides/${rideId}/legs/${legIndex}`,
  });

export const fetchOperatorsInvoices = (
  operatorId: string,
  params: OperatorsInvoicesPaginationQueryParams
): Promise<AxiosResponse<PageDto<Invoice[]>>> =>
  request({
    url: `/operators/${operatorId}/invoices`,
    params: new URLSearchParams(params),
  });

export const fetchOperatorsOrdersPricingDetails = (
  operatorId: string,
  bookingNumber: string
): Promise<AxiosResponse<PricingDetails>> =>
  request({
    url: `/operators/${operatorId}/orders/${bookingNumber}/pricing-details`,
  });

export const fetchOperatorsRidesActive = (
  operatorId: string,
  params: PaginationQueryParams
): Promise<AxiosResponse<PageDto<OperatorRideDtoSchemaType[]>>> =>
  request({
    url: `/operators/${operatorId}/rides/active`,
    params: new URLSearchParams(params),
  });

export const fetchOperatorsRidesFilter = (
  operatorId: string,
  params: PaginationQueryParams
): Promise<AxiosResponse<PageDto<OperatorRideDtoSchemaType[]>>> =>
  request({
    url: `/operators/${operatorId}/rides/filter`,
    params: new URLSearchParams(params),
  });

export const postOperatorsUsersRoles = (
  operatorId: string,
  userId: string,
  newRole: UserRole
): Promise<AxiosResponse<number>> => {
  return request({
    method: 'POST',
    url: `/operators/${operatorId}/users/${userId}/roles/${newRole}`,
  });
};

export const postOperatorsRidesFinish = (
  operatorId: string,
  data: ChangeRideEndSchemaType
): Promise<AxiosResponse<RideDtoSchemaType>> => {
  return request({
    method: 'POST',
    url: `/operators/${operatorId}/rides/${data.rideId}/finish`,
    data,
  });
};

export const fetchOperatorsVehicles = (
  operatorId: string,
  params: OperatorsVehiclesPaginationQueryParams
): Promise<AxiosResponse<PageDto<Vehicle[]>>> =>
  request({
    url: `/operators/${operatorId}/vehicles/filter`,
    params: new URLSearchParams(params),
  });

export const fetchOperatorsDrivers = (
  operatorId: string,
  params: OperatorsDriversPaginationQueryParams
): Promise<AxiosResponse<PageDto<Driver[]>>> =>
  request({
    url: `/operators/${operatorId}/drivers/filter`,
    params: new URLSearchParams(params),
  });

export const fetchOperatorsDriversById = (
  operatorId: string,
  driverId: string
): Promise<AxiosResponse<DriverDtoSchema>> =>
  request({
    url: `/operators/${operatorId}/drivers/${driverId}`,
  });

export const deleteOperatorsById = (
  operatorId: string,
  userId: string
): Promise<AxiosResponse<number>> =>
  request({
    method: 'DELETE',
    url: `/operators/${operatorId}/users/${userId}`,
  });

export const deleteScheduleById = (
  operatorId: string,
  id: string
): Promise<AxiosResponse<number>> =>
  request({
    method: 'DELETE',
    url: `/operators/${operatorId}/schedule/${id}`,
  });

export const fetchOperatorsPricingRules = (
  operatorId: string
): Promise<AxiosResponse<PricingRulesDtoSchemaType>> =>
  request({
    url: `/operators/${operatorId}/pricing-rules`,
  });

export const putOperatorsPricingRules = (
  operatorId: string,
  data: PricingRulesDtoSchemaType
): Promise<AxiosResponse<PricingRulesDtoSchemaType>> =>
  request({
    method: 'PUT',
    url: `/operators/${operatorId}/pricing-rules`,
    data,
  });

export const fetchOperatorsVehicleSizes = (
  operatorId: string
): Promise<AxiosResponse<VehicleSize[]>> =>
  request({
    url: `/operators/${operatorId}/vehicle-sizes`,
  });

export const fetchOperatorsVehicleClasses = (
  operatorId: string
): Promise<AxiosResponse<VehicleClass[]>> =>
  request({
    url: `/operators/${operatorId}/vehicle-classes`,
  });

export const fetchOperatorsPricingSeasonalAdjustments = (
  operatorId: string,
  params: PaginationQueryParams
): Promise<AxiosResponse<PageDto<PricingPeriodSchemaType[]>>> =>
  request({
    url: `/operators/${operatorId}/pricing-seasonal-adjustments`,
    params: new URLSearchParams(params),
  });

export const postOperatorsPricingSeasonalAdjustments = (
  operatorId: string,
  data: PricingPeriodSchemaType
): Promise<AxiosResponse<PricingPeriodSchemaType>> =>
  request({
    method: 'POST',
    url: `/operators/${operatorId}/pricing-seasonal-adjustments`,
    data,
  });

export const putOperatorsPricingSeasonalAdjustments = (
  operatorId: string,
  data: PricingPeriodSchemaType
): Promise<AxiosResponse<PricingPeriodSchemaType>> =>
  request({
    method: 'PUT',
    url: `/operators/${operatorId}/pricing-seasonal-adjustments/${data.id}`,
    data,
  });

export const deleteOperatorsPricingSeasonalAdjustments = (
  operatorId: string,
  id: string
): Promise<AxiosResponse<number>> =>
  request({
    method: 'DELETE',
    url: `/operators/${operatorId}/pricing-seasonal-adjustments/${id}`,
  });

export const postOperatorsCalculator = (
  operatorId: string,
  data: PricingCalculatorSchemaType
): Promise<AxiosResponse<CalculatorResponseDtoSchemaType[]>> =>
  request({
    method: 'POST',
    url: `/operators/${operatorId}/calculator`,
    data,
  });

export const fetchSuppliersOperatorsClientsAll = (
  operatorId: string
): Promise<AxiosResponse<IDValueDtoLongString[]>> =>
  request({
    url: `/suppliers/operators/${operatorId}/clients/all`,
  });

export const fetchOperatorsFileDownloadUrl = (url: string): Promise<AxiosResponse<string>> =>
  request({
    url,
  });

export const fetchOperatorsRidesClientsExportCSV = (
  operatorId: string,
  clientId: string,
  params: ExportCSVQueryParams
): Promise<AxiosResponse<string>> =>
  request({
    url: `/operators/${operatorId}/rides/clients/${clientId}/export/csv`,
    params: new URLSearchParams(params),
  });

export const postOperatorsSchedule = (
  operatorId: string,
  data: ScheduleItemSchemaType
): Promise<AxiosResponse<ScheduleItem>> =>
  request({
    method: 'POST',
    url: `/operators/${operatorId}/schedule`,
    data,
  });

export const putOperatorsSchedule = (
  operatorId: string,
  ownerId: string,
  data: ScheduleItemSchemaType
): Promise<AxiosResponse<ScheduleItem>> =>
  request({
    method: 'PUT',
    url: `/operators/${operatorId}/schedule/${ownerId}`,
    data,
  });

export const fetchOrdersFiles = (bookingNumber: string): Promise<AxiosResponse<FileResponse[]>> =>
  request({
    url: `/orders/${bookingNumber}/files`,
  });

export const deleteOrdersFilesByFileName = (
  bookingNumber: string,
  fileName: string
): Promise<AxiosResponse<number>> =>
  request({
    method: 'DELETE',
    url: `/orders/${bookingNumber}/files/${fileName}`,
  });

export const postOrdersFiles = (
  bookingNumber: string,
  data: FormData
): Promise<AxiosResponse<FileResponse>> =>
  request({
    method: 'POST',
    url: `/orders/${bookingNumber}/files`,
    headers: { 'Content-Type': 'multipart/form-data' },
    data,
  });

export const fetchOperatorsFiles = (operatorId: string): Promise<AxiosResponse<FileType[]>> =>
  request({
    url: `/operators/${operatorId}/operator/files`,
  });

export const deleteOperatorsFilesByFileName = (
  operatorId: string,
  fileName: string
): Promise<AxiosResponse<number>> =>
  request({
    method: 'DELETE',
    url: `/operators/${operatorId}/files/${fileName}`,
  });

export const postOperatorsFiles = (
  operatorId: string,
  data: FormData
): Promise<AxiosResponse<FileType[]>> =>
  request({
    method: 'POST',
    url: `/operators/${operatorId}/files`,
    headers: { 'Content-Type': 'multipart/form-data' },
    data,
  });

export const fetchOperatorsBankAccount = (
  operatorId: string
): Promise<AxiosResponse<OperatorBankAccountDtoSchemaType>> =>
  request({
    url: `/operators/${operatorId}/bankaccount`,
  });

export const putOperatorsBankAccount = (
  operatorId: string,
  data: OperatorBankAccountDtoSchemaType
): Promise<AxiosResponse<OperatorBankAccountDtoSchemaType>> =>
  request({
    method: 'PUT',
    url: `/operators/${operatorId}/bankaccount`,
    data,
  });

export const deleteOperatorsBankAccount = (operatorId: string): Promise<AxiosResponse<number>> =>
  request({
    method: 'DELETE',
    url: `/operators/${operatorId}/bankaccount`,
  });

export const fetchOperatorsVehiclesById = (
  operatorId: string,
  vehicleId: string
): Promise<AxiosResponse<VehicleDtoSchema>> =>
  request({
    url: `/operators/${operatorId}/vehicles/${vehicleId}`,
  });

export const postOperatorsVehiclesDriversById = (
  operatorId: string,
  vehicleId: string,
  driverId: string
): Promise<AxiosResponse<VehicleDtoSchema>> =>
  request({
    method: 'POST',
    url: `/operators/${operatorId}/vehicles/${vehicleId}/drivers/${driverId}`,
  });

export const fetchOperatorsVehiclesFiles = (
  operatorId: string,
  vehicleId: string
): Promise<AxiosResponse<FileType[]>> =>
  request({
    url: `/operators/${operatorId}/vehicles/${vehicleId}/files`,
  });

export const postOperatorsVehiclesFilesPhotosRank = (
  operatorId: string,
  vehicleId: string,
  data: FileResponse[]
): Promise<AxiosResponse<FileType[]>> =>
  request({
    method: 'POST',
    url: `/operators/${operatorId}/vehicles/${vehicleId}/files/photos/rank`,
    data,
  });

export const fetchOperatorsVehiclesTemporaryBase = (
  operatorId: string,
  vehicleId: string,
  params: PaginationQueryParams
): Promise<AxiosResponse<PageDto<VehicleTemporaryBase[]>>> =>
  request({
    url: `/operators/${operatorId}/vehicles/${vehicleId}/temporary-base`,
    params: new URLSearchParams(params),
  });

export const fetchOperatorsRidesDriverCandidates = (
  operatorId: string,
  rideId: string
): Promise<AxiosResponse<DriverCandidate[]>> =>
  request({
    url: `/operators/${operatorId}/rides/${rideId}/driver-candidates`,
  });

export const fetchOperatorsDriversCandidates = (
  operatorId: string
): Promise<AxiosResponse<DriverCandidate[]>> =>
  request({
    url: `/operators/${operatorId}/driver-candidates`,
  });

export const fetchOperatorsRidesVehicleCandidates = (
  operatorId: string,
  rideId: string
): Promise<AxiosResponse<VehicleCandidate[]>> =>
  request({
    url: `/operators/${operatorId}/rides/${rideId}/vehicle-candidates`,
  });

export const fetchOperatorsVehiclesAll = (operatorId: string): Promise<AxiosResponse<Vehicle[]>> =>
  request({
    url: `/operators/${operatorId}/vehicles/all`,
  });

export const fetchOperatorsDriversFiles = (
  operatorId: string,
  driverId: string
): Promise<AxiosResponse<FileType[]>> =>
  request({
    url: `/operators/${operatorId}/drivers/${driverId}/files`,
  });

export const fetchOperatorsScheduleDriversVacationLog = (
  operatorId: string,
  driverId: string,
  params: PaginationQueryParams
): Promise<AxiosResponse<PageDto<ScheduleItem>>> =>
  request({
    url: `/operators/${operatorId}/schedule/drivers/${driverId}/vacation-log`,
    params: new URLSearchParams(params),
  });

export const fetchOperatorsAlerts = (
  operatorId: string,
  params: PaginationQueryParams
): Promise<AxiosResponse<PageDto<Alert[]>>> =>
  request({
    url: `/operators/${operatorId}/alerts`,
    params: new URLSearchParams(params),
  });

export const postOperatorsAlertsStatus = (
  operatorId: string,
  alertId: string,
  status: SeenStatus
): Promise<AxiosResponse<number>> =>
  request({
    method: 'POST',
    url: `/operators/${operatorId}/alerts/${alertId}/status/${status}`,
  });

export const fetchOperatorsUserMessagingMessages = (
  operatorId: string,
  params: OperatorsUserMessagingMessagesQueryParams
): Promise<AxiosResponse<PageDto<UserMessage[]>>> =>
  request({
    url: `/operators/${operatorId}/usermessaging/messages`,
    params: new URLSearchParams(params),
  });

export const fetchOperatorsUserMessagingUsers = (
  operatorId: string,
  params: OperatorsUserMessagingUsersQueryParams
): Promise<AxiosResponse<PageDto<MessageGroupMember[]>>> =>
  request({
    url: `/operators/${operatorId}/usermessaging/users`,
    params: new URLSearchParams(params),
  });

export const fetchOperatorsUserMessagingGroups = (
  operatorId: string
): Promise<AxiosResponse<MessageGroup[]>> =>
  request({
    url: `/operators/${operatorId}/usermessaging/groups`,
  });

export const fetchOperatorsUserMessagingGroupsMembers = (
  operatorId: string,
  groupId: string
): Promise<AxiosResponse<PageDto<MessageGroupMember[]>>> =>
  request({
    url: `/operators/${operatorId}/usermessaging/groups/${groupId}/members`,
  });

export const postOperatorsUserMessagingGroupsMembers = (
  operatorId: string,
  groupId: string,
  data: number[]
): Promise<AxiosResponse<MessageGroupSchemaType[]>> =>
  request({
    method: 'POST',
    url: `/operators/${operatorId}/usermessaging/groups/${groupId}/members`,
    data,
  });

export const postOperatorsOffers = (
  operatorId: string,
  data: RideOffersSchemaType
): Promise<AxiosResponse<OfferSchemaType>> =>
  request({
    method: 'POST',
    url: `/operators/${operatorId}/offers`,
    data,
  });

export const postOperatorsOffersPrice = (
  operatorId: string,
  data: OfferPriceRequest
): Promise<AxiosResponse<OfferSchemaType>> =>
  request({
    method: 'POST',
    url: `/operators/${operatorId}/offers/${data.refNumber}/price`,
    data,
  });

export const postOperatorsOffersEmail = (
  operatorId: string,
  data: SendOfferToClientRequestSchemaType
): Promise<AxiosResponse<OfferSchemaType>> =>
  request({
    method: 'POST',
    url: `/operators/${operatorId}/offers/${data.refNumber}/email`,
    data,
  });

export const postOperatorsVehiclesActivation = (
  operatorId: string,
  data: VehicleActivationDto
): Promise<AxiosResponse<BusStatus>> =>
  request({
    method: 'POST',
    url: `/operators/${operatorId}/vehicles/activation`,
    data,
  });

export const postOperatorsInvitation = (
  operatorId: string,
  data: AccountUserSchemaType
): Promise<AxiosResponse<number>> =>
  request({
    method: 'POST',
    url: `/operators/${operatorId}/invitation`,
    data,
  });

export const postInvitationsStatus = (
  data: string
): Promise<AxiosResponse<TokenStatusResponseSchemaType>> =>
  request({
    method: 'POST',
    url: `/invitations/status`,
    data,
  });

export const postRegistrationOperator = (
  data: OperatorRegistrationPayload
): Promise<AxiosResponse<RegistrationResponseSchemaType>> => {
  return request({
    method: 'POST',
    url: `/registration/operator`,
    data,
  });
};

export const postRegistrationOperatorUser = (
  data: AccountInvitationRegistrationSchemaType
): Promise<AxiosResponse<RegistrationResponseSchemaType>> => {
  return request({
    method: 'POST',
    url: `/registration/operator-user`,
    data,
  });
};

export const deleteOperatorsUserMessagingGroupsMembersById = (
  operatorId: string,
  groupId: string,
  userId: string
): Promise<AxiosResponse<number>> =>
  request({
    method: 'DELETE',
    url: `/operators/${operatorId}/usermessaging/groups/${groupId}/members/${userId}`,
  });

export const putOperatorsUserMessagingGroupsById = (
  operatorId: string,
  groupId: string,
  data: MessageGroupSchemaType
): Promise<AxiosResponse<MessageGroupSchemaType>> => {
  return request({
    method: 'PUT',
    url: `/operators/${operatorId}/usermessaging/groups/${groupId}`,
    data,
  });
};

export const postOperatorsUserMessagingGroupsArchive = (
  operatorId: string,
  groupId: string
): Promise<AxiosResponse<number>> =>
  request({
    method: 'POST',
    url: `/operators/${operatorId}/usermessaging/groups/${groupId}/archive`,
  });

export const postOperatorsRidesMove = (
  operatorId: string,
  rideId: string,
  data: RideMoveRequest
): Promise<AxiosResponse<RideWithScheduleItem>> =>
  request({
    method: 'POST',
    url: `/operators/${operatorId}/rides/${rideId}/move`,
    data,
  });

export const postOperatorsRidesDriversMove = (
  operatorId: string,
  rideId: string,
  driverId: string
): Promise<AxiosResponse<RideWithScheduleItem>> =>
  request({
    method: 'POST',
    url: `/operators/${operatorId}/rides/${rideId}/drivers/${driverId}`,
  });

export const postOperatorsUserMessagingGroups = (
  operatorId: string,
  data: NewMessageGroupSchemaType
): Promise<AxiosResponse<MessageGroupSchemaType[]>> =>
  request({
    method: 'POST',
    url: `/operators/${operatorId}/usermessaging/groups`,
    data,
  });

export const postOperatorsUserMessagingMessages = (
  operatorId: string,
  data: UserMessageDtoSchemaType
): Promise<AxiosResponse<UserMessageDtoSchemaType>> =>
  request({
    method: 'POST',
    url: `/operators/${operatorId}/usermessaging/messages`,
    data,
  });

export const postOperatorsDrivers = (
  operatorId: string,
  data: DriverDtoSchema
): Promise<AxiosResponse<DriverWithStatusDtoSchemaType>> =>
  request({
    method: 'POST',
    url: `/operators/${operatorId}/drivers`,
    data,
  });

export const putOperatorsDrivers = (
  operatorId: string,
  data: DriverDtoSchema
): Promise<AxiosResponse<DriverDtoSchema>> =>
  request({
    method: 'PUT',
    url: `/operators/${operatorId}/drivers/${data.id}`,
    data,
  });

export const postOperatorsVehicles = (
  operatorId: string,
  data: VehicleDtoSchema
): Promise<AxiosResponse<VehicleWithStatusDtoSchema>> =>
  request({
    method: 'POST',
    url: `/operators/${operatorId}/vehicles`,
    data,
  });

export const putOperatorsVehicles = (
  operatorId: string,
  data: VehicleDtoSchema
): Promise<AxiosResponse<VehicleDtoSchema>> =>
  request({
    method: 'PUT',
    url: `/operators/${operatorId}/vehicles/${data.id}`,
    data,
  });

export const postOperatorsVehiclesTemporaryBase = (
  operatorId: string,
  vehicleId: string,
  data: VehicleTemporaryBaseDtoSchemaType
): Promise<AxiosResponse<VehicleTemporaryBaseDtoSchemaType>> =>
  request({
    method: 'POST',
    url: `/operators/${operatorId}/vehicles/${vehicleId}/temporary-base`,
    data,
  });

export const putOperatorsVehiclesTemporaryBase = (
  operatorId: string,
  vehicleId: string,
  data: VehicleTemporaryBaseDtoSchemaType
): Promise<AxiosResponse<VehicleTemporaryBaseDtoSchemaType>> =>
  request({
    method: 'PUT',
    url: `/operators/${operatorId}/vehicles/${vehicleId}/temporary-base/${data.id}`,
    data,
  });

export const deleteOperatorsVehiclesTemporaryBase = (
  operatorId: string,
  vehicleId: string,
  baseId: string
): Promise<AxiosResponse<number>> =>
  request({
    method: 'DELETE',
    url: `/operators/${operatorId}/vehicles/${vehicleId}/temporary-base/${baseId}`,
  });

export const deleteOperatorsVehiclesById = (
  operatorId: string,
  vehicleId: string
): Promise<AxiosResponse<number>> =>
  request({
    method: 'DELETE',
    url: `/operators/${operatorId}/vehicles/${vehicleId}`,
  });

export const fetchOperatorsAlertsCountNew = (operatorId: string): Promise<AxiosResponse<number>> =>
  request({
    url: `/operators/${operatorId}/alerts/count-new`,
  });

export const postTripsItineraryCalculator = (
  data: ItineraryRequest
): Promise<AxiosResponse<ItineraryResponse>> =>
  request({
    method: 'POST',
    url: `/trips/itinerary-calculator`,
    data,
  });

export const fetchOperatorsVehiclesLocations = (
  operatorId: string,
  params: OperatorsVehiclesLocationsQueryParams
): Promise<AxiosResponse<VehicleLocation[]>> =>
  request({
    url: `/operators/${operatorId}/vehicles/locations/filter`,
    params: new URLSearchParams(params),
  });

export const postOperatorsDriversInvitation = (
  operatorId: string,
  driverId: string
): Promise<AxiosResponse<number>> =>
  request({
    method: 'POST',
    url: `/operators/${operatorId}/drivers/${driverId}/invitation`,
  });

export const deleteOperatorsDriversById = (
  operatorId: string,
  driverId: string
): Promise<AxiosResponse<number>> =>
  request({
    method: 'DELETE',
    url: `/operators/${operatorId}/drivers/${driverId}`,
  });

export const fetchSuppliersOperatorsClientsContacts = (
  operatorId: string,
  clientId: string
): Promise<AxiosResponse<ContactInfoSchemaType[]>> =>
  request({
    url: `/suppliers/operators/${operatorId}/clients/${clientId}/contacts`,
  });

export const postCorporateChartersItineraryCalculator = (
  data: CorporateCharterItinerarySchemaType
): Promise<AxiosResponse<StopBasedItinerary>> =>
  request({
    method: 'POST',
    url: `/corporate-charters/itinerary-calculator`,
    data,
  });

export const postOperatorsCorporateCharters = (
  operatorId: string,
  data: CorporateCharterRequest
): Promise<AxiosResponse<CorporateCharter>> =>
  request({
    method: 'POST',
    url: `/operators/${operatorId}/corporate-charters`,
    data,
  });

export const fetchOperatorsOperatorTokenByAccessToken = (
  accessToken: string
): Promise<AxiosResponse<OperatorDtoSchemaType>> =>
  request({
    url: `/operators/operator-token/${accessToken}`,
  });

export const fetchOperatorOffersByOperatorId = (
  operatorId: string,
  params: PaginationQueryParams
): Promise<AxiosResponse<PageDto<OperatorOfferWithRank[]>>> =>
  request({
    url: `/operators/${operatorId}/operator-offers`,
    params: new URLSearchParams(params),
  });

export const fetchOperatorOffersByRefNumber = (
  refNumber: string
): Promise<AxiosResponse<OperatorOfferByRefNumber[]>> =>
  request({
    url: `/offers/${refNumber}/operator-offers`,
  });

export const fetchOperatorsPlansActive = (
  operatorId: string
): Promise<AxiosResponse<OperatorPlanDto>> =>
  request({
    url: `/operators/${operatorId}/plans/active`,
  });

export const fetchPlans = (): Promise<AxiosResponse<PageDto<PlanDto[]>>> =>
  request({
    url: `/plans`,
  });

export const postFeedback = (data: FeedbackRequestSchemaType): Promise<AxiosResponse<number>> =>
  request({
    method: 'POST',
    url: `/feedback`,
    data,
  });

export const fetchOperatorsMileageRevenueSummary = (
  operatorId: string
): Promise<AxiosResponse<MileageRevenueSummary>> =>
  request({
    url: `/operators/${operatorId}/mileage-revenue-summary`,
  });

export const fetchOperatorsClients = (
  operatorId: string,
  params: PaginationQueryParams
): Promise<AxiosResponse<PageDto<ClientSchemaType[]>>> =>
  request({
    url: `/operators/${operatorId}/clients`,
    params: new URLSearchParams(params),
  });

export const fetchOperatorsClientsAll = (
  operatorId: string
): Promise<AxiosResponse<IDValueDtoLongString[]>> =>
  request({
    url: `/operators/${operatorId}/clients/all`,
  });

export const postOperatorsClients = (
  operatorId: string,
  data: ClientSchemaType
): Promise<AxiosResponse<ClientSchemaType>> =>
  request({
    method: 'POST',
    url: `/operators/${operatorId}/clients`,
    data,
  });

export const putOperatorsClient = (
  operatorId: string,
  data: ClientSchemaType
): Promise<AxiosResponse<ClientSchemaType>> =>
  request({
    method: 'PUT',
    url: `/operators/${operatorId}/clients/${data.id}`,
    data,
  });
